import type { BLOCK_TYPE } from '../enums'
import type { ReferenceId } from '../ids'
import type { BlockReference } from '../references'

import type { IBlock, BlockBaseSettings } from './base'

export enum CATALOG {
  /**
   * Fetches products from epilot
   */
  EPILOT = 'epilot',
  /**
   * Fetches products from external integration
   */
  EXTERNAL = 'external'
}

export enum PRODUCTS_TYPE {
  /**
   * Offer users a static selection of products
   */
  STATIC = 'static',
  /**
   * Product Selection blocks in your Journey
   */
  CROSS_SELLING_ALL = 'cross-selling-all',
  /**
   * Offer users products that are related to products previously selected
   *
   * The related blocks are referenced in @references
   */
  CROSS_SELLING_SELECTED = 'cross-selling-selected'
}

export enum RECURRENCE_UNIT {
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  HALF_YEARLY = 'every_6_months',
  YEARLY = 'yearly'
}

export enum TILE_ALIGNMENT {
  CENTER = 'center',
  LEFT = 'left',
  EVENLY_DISTRIBUTED = 'evenly-distributed'
}

export enum CTA_OPTION {
  ADD_TO_CART = 'add-to-cart',
  SELECT = 'select',
  SUBSCRIBE = 'subscribe',
  REQUEST_QUOTE = 'request-quote'
}

export enum SELECTION_OPTION {
  /**
   * Select One Product
   */
  ONE = 'one',
  /**
   * Select each product once
   */
  EACH_ONE = 'each-1',
  /**
   * Select each product N times
   */
  EACH_N = 'each-n'
}

export interface TileLayoutProperty {
  /**
   * Index used to display the property on the product tile.
   */
  order: number
  /**
   * Weather the property is allowed to be configured
   */
  isConfigurable: boolean
  /**
   * Weather this property is visible in the tile layout.
   */
  isVisible: boolean
  /**
   * Key used to map this property to UI in journey app and journey builder.
   */
  key: string
}

export interface TileLayout {
  /**
   * Name of the layout variant. Current values are 1, 2, 3, 4, 5
   * The tile layout properties are hardcoded for each variant in a specified order.
   */
  variant: string
  /**
   * Properties of a product. Each property defines it's order, visibility and configurability
   */
  properties: {
    /**
     * Title of the product
     */
    headline: TileLayoutProperty
    /**
     * Image of the product
     */
    image: TileLayoutProperty
    /**
     * Description of the product
     */
    description: TileLayoutProperty
    /**
     * Price of the product
     */
    price: TileLayoutProperty & {
      /**
       * Weather to display trailing zeros in the price.
       * Assuming a price of $10, when true the price will be $10.00, otherwise $10
       *
       * Open question: What value does this config bring, can we not always show (or not show) trailing zeros in the price?
       */
      showTrailingDecimalZeros: boolean
      /**
       * Weather to show a recurring total or a fixed one-time price
       */
      showRecurringTotal: boolean
      /**
       * When showRecurringTotal is true, the interval is defined with recurrenceUnit.
       */
      recurrenceUnit?: RECURRENCE_UNIT
      /**
       * Wether to show unitary average price
       */
      showUnitaryAverage: boolean
      /**
       * Price after cashback is always visible, this toggle will highlight it for the end-customer
       */
      featurePriceAfterCashback: boolean
    }
    /**
     * Breakdown of the price into components
     */
    priceComponents: TileLayoutProperty & {
      /**
       * Weather to display trailing zeros in the price.
       * Assuming a price of $10, when true the price will be $10.00, otherwise $10
       */
      showTrailingDecimalZeros: boolean
    }
    /**
     * Call to action button
     */
    cta: TileLayoutProperty & {
      /**
       * Choice of "call to action" labels for the button on the product tile.
       */
      option: CTA_OPTION
    }
    /**
     * Features of products
     */
    features: TileLayoutProperty & {
      /**
       * Maximum number of features to be displayed in the product tile.
       */
      maxCount: number
    }
    /**
     * "More details" button which opens a dialog with additional details
     */
    details: TileLayoutProperty & {
      /**
       * Weather to show the market data charts in the price details dialog
       */
      showMarketDataCharts: boolean
    }
    /**
     * Files attached to the product
     */
    files: TileLayoutProperty
  }
}

/**
 * A product filter is a filter that EXCLUDES a product from the product block.
 * It is used to compare the value of a referenced block against the value of the product filter.
 */
export type ProductFilter = {
  /**
   * The product ID of the product to be filtered
   */
  productId: string
  /**
   * The price ID of the product to be filtered
   */
  priceId: string
  /**
   * This value will be compared against the referenced block's value to exclude the products.
   * Please note that we could not migrate the binary references as boolean values so will get string values as "true" or "false"
   *
   * @description When the comparison returns:
   * - true: the product is excluded/filtered out from the block
   * - false: the product is included in the block
   *
   * @example (Boolean block)
   *   - If the referenced block's value is "true" and value is "true", the product will be excluded from the block
   *   - If the referenced block's value is "true" and value is "false", the product will be included in the block
   *
   * @example (Single choice block)
   *   - If the referenced block's value is "Option 1" and value is "Option 1", the product will be excluded from the block
   *   - If the referenced block's value is "Option 2" and value is "Option 2", the product will be included in the block
   */
  excludeOnValue: string | boolean
}

export interface ProductReference {
  /**
   * The product ID of the products to be displayed in the block
   */
  productId: string
  /**
   * The price ID of the products to be displayed in the block
   */
  priceId: string
}

export interface ProductProperties extends ProductReference {
  /**
   * Weather product is featured in the UI
   */
  isFeatured: boolean
  /**
   * Optional settings for featured products when @isFeatured is true
   */
  featureAppearance?: {
    /**
     * The text for featured products
     * @example "Top Seller"
     */
    labelText?: string
    /**
     * The color of the border
     * @example "#FF0000"
     * @description The color of the border in hex format
     */
    borderColor?: string
    /**
     * The text color of the label
     * @example "#FFFFFF"
     * @description The text color of the label in hex format
     */
    labelColor?: string
  }
}

/**
 * General settings for Products Block
 */
export interface ProductsBlockSettings {
  /**
   * Properties for the Products displayed in a Tile Layout.
   */
  layout: TileLayout
  /**
   * Alignment of tiles in a product block
   */
  alignment: TILE_ALIGNMENT
  /**
   * Selection setting for products.
   * This enables users to configure the amount and quantity (one or many) of the products to be selected.
   */
  selection: SELECTION_OPTION
}

/**
 * Product Block settings with a product catalog from external source (integration).
 */
export interface ExternalCatalogSettings extends ProductsBlockSettings {
  catalog: CATALOG.EXTERNAL
  integrationId: string
}

/**
 * Product Block settings with a product catalog from epilot.
 */
export interface EpilotCatalogSettings extends ProductsBlockSettings {
  catalog: CATALOG.EPILOT
  productsType: PRODUCTS_TYPE
  products: ProductProperties[]
}

interface ICatalogBasedProductsBlock extends IBlock {
  type: BLOCK_TYPE.PRODUCTS
  required: false
  settings: BlockBaseSettings & {}
}

export interface CrossSellingProductsReference extends BlockReference {
  meaning: 'cross-selling-product'
  products: ProductReference[]
  enabled: boolean
}

/**
 * Reference to a simple price
 */
export interface SimplePrice {
  priceId: string
}

/**
 * Reference to price component of a composite price.
 */
export interface CompositePriceComponent {
  priceId: string
  componentId: string
}

/**
 * Mapping metadata for direct input mapping
 */
export interface DirectMappingMetadata {
  productId: string
  price: SimplePrice | CompositePriceComponent
}

/**
 * Mapping metadata for indirect input mapping
 */
export interface IndirectMappingMetadata {
  unit: string | undefined
  recurrenceType: 'one-time' | 'recurring' | 'any'
}

/**
 * A Variable Price Reference points to a number input block that provides the value
 * used to calculate the final price. This value is multiplied by a specific price or
 * price component to determine the final amount. Mappings are either direct (specific
 * for a given product/price pair) or indirect (for all prices with a given unit and
 * recurrence type).
 */
export interface VariablePriceReference extends BlockReference {
  meaning: 'variable-price'
  metadata: DirectMappingMetadata | IndirectMappingMetadata
}

/**
 * An Option Price Reference points to a binary block that indicates whether
 * a specific price component should be included or excluded for a given product.
 *
 * If `value` is true, the price component will be included when binary block is
 * checked and excluded when unchecked
 * If `value` is false, it's vise versa.
 */
export interface OptionalPriceComponentReference extends BlockReference {
  meaning: 'optional-price'
  operator: 'EQUALS'
  value: boolean
  metadata: {
    productId: string
    price: CompositePriceComponent
  }
}

/**
 * The GridFeeAreaReference is a reference to an Availability or Address block in
 * order to use their address data for the Country-wide Grid Fees integration.
 */
export interface GridFeeAreaReference extends BlockReference {
  meaning: 'grid-fee-area'
}

export interface ProductFilterReference extends BlockReference {
  meaning: 'product-filter'
  filters: ProductFilter[]
}

export interface ProductsAvailabilityCheckReference extends BlockReference {
  meaning: 'availability check for products'
}

export type ProductsBlockOwnReferences =
  | OptionalPriceComponentReference
  | VariablePriceReference
  | CrossSellingProductsReference
  | GridFeeAreaReference
  | ProductFilterReference
  | ProductsAvailabilityCheckReference

export interface EpilotCatalogProductsBlock extends ICatalogBasedProductsBlock {
  settings: BlockBaseSettings &
    EpilotCatalogSettings & {
      catalog: CATALOG.EPILOT
    }
  /**
   * references to other feature of the journey required to fetch catalog and products
   */
  references: Record<ReferenceId, ProductsBlockOwnReferences>
}

export interface ExternalCatalogProductsBlock
  extends ICatalogBasedProductsBlock {
  settings: BlockBaseSettings &
    ExternalCatalogSettings & {
      catalog: CATALOG.EXTERNAL
    }
  references?: never
}

export type ProductsBlock =
  | EpilotCatalogProductsBlock
  | ExternalCatalogProductsBlock
